import { useGetLookupRelatedItemQuery } from '@crew/apis/lookup/lookupApis'
import { GetRelatedItemRequest } from '@crew/apis/lookup/models/getRelatedItem/request'
import { EntityType } from '@crew/enums/domain'
import { skipToken } from '@reduxjs/toolkit/query'
import React, { useMemo } from 'react'
import { CrewEntityIcon } from '../crewEntityIcon/crewEntityIcon'
import { getDefaultTabValue } from '@crew/utils/enum'
import {
  EventDetailTabs,
  FileDetailListTabs,
  TaskDetailListTabs,
} from 'enums/app'
import { entityReference as entityReferenceConst } from '@crew/configs/constants'

type EntityReferenceProps = {
  entityType: EntityType
  entityRecordId: string
}

// チャットメッセージ内で使用するコンポーネント、メッセージ内からタスクやイベントとの関連付けを表示する
export const CrewEntityReference: React.FC<EntityReferenceProps> = (props) => {
  const { entityType, entityRecordId } = props

  // 関連アイテムを取得
  const getLookupRelatedItemQuery: GetRelatedItemRequest | undefined = {
    entityType: entityType,
    id: entityRecordId,
  }

  const {
    data: result,
    error,
    isLoading,
  } = useGetLookupRelatedItemQuery(getLookupRelatedItemQuery ?? skipToken)

  // 参照先の名前
  const entityName = useMemo(() => {
    // ロード中はスペースを返す
    if (isLoading) return ' '

    return result?.relatedItem?.name ?? ' '
  }, [isLoading, result])

  // EntityTypeに応じたURLとアイコンを返す
  const entityLink = useMemo(() => {
    switch (entityType) {
      case EntityType.Event:
        return `/events/${entityRecordId}/${getDefaultTabValue(EventDetailTabs)}`
      case EntityType.Task:
        return `/tasks/${entityRecordId}/${getDefaultTabValue(TaskDetailListTabs)}`
      case EntityType.File:
        return `/projects/${result?.relatedItem?.entityRecordId}/files/${entityRecordId}/${getDefaultTabValue(FileDetailListTabs)}`
      default:
        return ''
    }
  }, [entityType, entityRecordId, result])

  return (
    <>
      {error ? (
        <span>{`!${entityType}:${entityRecordId}`}</span>
      ) : (
        <a
          href={entityLink}
          className="crew-entity-reference box-decoration-slice"
          target="_blank"
          rel="noreferrer"
          // 以下の3属性は、CrewHtmlContent でこのCrewEntityReferenceを生成し、Lexical上にペーストしたときに、Nodeに変換する際に必要
          // entityReferenceNodeの、importDOMを参照
          data-tag-type={entityReferenceConst.DATA_ATTRIBUTE.TAG_TYPE.VALUE}
          data-entity-type={entityType}
          data-entity-record-id={entityRecordId}
        >
          <CrewEntityIcon
            entityType={entityType}
            width={18}
            height={18}
            className="mr-1 align-text-bottom inline-block"
          />
          <span>{entityName}</span>
        </a>
      )}
    </>
  )
}
